import { createSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import { useTimeoutFn, useAsyncFn } from 'react-use';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { isSoftLoggedOut } from 'reducers/Login';
import { getHasUndismissedErrorsOfType } from 'reducers/Error';
import { getSetting } from 'reducers/configs/settings';
import { getLastSyncTimeStamp } from 'reducers/essentialData';
import { invalidateAll } from 'containers/EssentialDataLoader/utils';
import { useConfirmation } from 'components/Confirmation';
import { setShouldFetchEssentialData } from 'actions/essentialData';
import { getComponents } from 'reducers/modalPage';
import { getIsDefaultCustomer } from 'reducers/customerSearch';
import { getHasProductsInShoppingCart } from 'reducers/ShoppingCart';
import { modalPages } from 'constants/modalPage';
import { getIsOnlineAndLoggedIn } from 'reducers/connectivity/connection';

const getCanPromptForEssentialDataResync = createSelector(
  getComponents,
  getIsDefaultCustomer,
  getHasProductsInShoppingCart,
  getHasUndismissedErrorsOfType('savingDocument'),
  getIsOnlineAndLoggedIn,
  isSoftLoggedOut,
  (
    openedModalPages,
    hasDefaultCustomerSelected,
    hasItemsInCart,
    isSavingSaleDocument,
    isOnlineAndLoggedIn,
    isLocked,
  ) => {
    const openedModalNames = openedModalPages.map(mp => mp.component);
    const blockingModalNames = [
      modalPages.Settings,
      modalPages.OpenDay,
      modalPages.closeDay,
      modalPages.stockAndPriceItem,
      modalPages.stockAndPriceList,
      modalPages.ZReport,
      modalPages.createCustomer,
      modalPages.extraAddress,
      modalPages.recentSales,
      modalPages.pendingSales,
      modalPages.pickupOrders,
      modalPages.layawayList,
      modalPages.layawayActionSelection,
      modalPages.offers,
      modalPages.unpaidInvoices,
      modalPages.ProductReturn,
      modalPages.Payment,
      modalPages.PaymentConfirmation,
    ];
    return (
      hasDefaultCustomerSelected &&
      !hasItemsInCart &&
      !isSavingSaleDocument &&
      !openedModalNames.some(name => blockingModalNames.includes(name)) &&
      isOnlineAndLoggedIn &&
      !isLocked
    );
  },
);

export function useEssentialDataExpiration() {
  const dispatch = useDispatch();
  const confirm = useConfirmation();
  const { t } = useTranslation('essentialData');

  const lastSyncTime = useSelector(getLastSyncTimeStamp);
  const timeUntilInvalidation = useSelector(
    getSetting('time_until_essential_data_invalidation'),
  );
  const canPromptForResync = useSelector(getCanPromptForEssentialDataResync);

  const [isExpired, setIsExpired] = useState(false);
  const cachedLastSyncTime = useRef(0);

  const timeOfInvalidation = lastSyncTime + timeUntilInvalidation * 3600;
  const now = new Date().getTime();
  const remainingTime = timeOfInvalidation * 1000 - now;

  useTimeoutFn(() => setIsExpired(true), remainingTime > 0 ? remainingTime : 0);

  const [
    { loading: isPromptingForResync },
    invalidateAndPromptToResync,
  ] = useAsyncFn(async () => {
    await dispatch(invalidateAll());

    if (lastSyncTime) {
      cachedLastSyncTime.current = lastSyncTime;
    }
    const hoursSinceLastSync = dayjs().diff(
      dayjs.unix(cachedLastSyncTime.current),
      'hour',
    );

    const shouldResync = await confirm({
      title: t('resyncPrompt.title'),
      body: cachedLastSyncTime.current
        ? t('resyncPrompt.body.lastSyncExpired', { hoursSinceLastSync })
        : t('resyncPrompt.body.lastSyncUnknown'),
      confirmText: t('resyncPrompt.resync'),
      cancelText: t('resyncPrompt.postpone'),
    }).then(
      () => true,
      () => false,
    );
    if (shouldResync) {
      dispatch(setShouldFetchEssentialData(true));
    } else {
      setIsExpired(false);
      setTimeout(() => setIsExpired(true), 300e3); // postpone by 5 minutes
    }
  }, [dispatch, confirm, lastSyncTime]);

  useEffect(() => {
    if (isExpired && canPromptForResync && !isPromptingForResync) {
      invalidateAndPromptToResync();
    }
  }, [
    isExpired,
    canPromptForResync,
    isPromptingForResync,
    invalidateAndPromptToResync,
  ]);
}
